import { usePageSeo } from "@/hooks/usePageSeo";
import { useLang } from "@/context/LanguageContext";
import { PageHero } from "@/components/common/PageHero";
import { CTASection } from "@/components/common/CTASection";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  ShieldCheck, MonitorSmartphone, Headphones, Globe2, GraduationCap,
  LineChart, MapPin, Handshake, Eye, ShieldAlert,
} from "lucide-react";

const ICONS = [ShieldCheck, MonitorSmartphone, Headphones, Globe2, GraduationCap, LineChart, MapPin, Handshake, Eye, ShieldAlert];

const BLOCKS_AR = [
  ["اختيار الوسطاء", "نبني علاقات وساطة بناءً على عوامل تشمل الوضع التنظيمي والتقنية والوصول للأسواق ودعم العملاء والقدرات التشغيلية — لتتخذ خيارات أكثر اطلاعاً."],
  ["تقنيات التداول", "الوصول لمنصات تداول احترافية يقدمها وسطاء مختارون، بما في ذلك MetaTrader وحلول الويب والجوال الحديثة."],
  ["دعم العملاء", "احصل على مساعدة مخصصة طوال رحلة اختيار الوسيط والتسجيل، مع فريق يفهم المنطقة."],
  ["الوصول للأسواق", "استكشف الفوركس والسلع والمؤشرات وأدوات الأسهم عبر حلول الوساطة المتاحة من شركائنا."],
  ["التعليم", "طوّر معرفتك بالأسواق عبر دروس منظمة تشمل المستويات المبتدئة والمتوسطة والمتقدمة."],
  ["ذكاء السوق", "ابقَ مطلعاً بتحديثات السوق والتحليل الفني والرؤى الاقتصادية من فريق أبحاثنا."],
  ["الخبرة الإقليمية", "استفد من أعمال تركز على منطقة الشرق الأوسط مع ربط المتداولين بالأسواق العالمية."],
  ["منظومة الشراكة", "انضم لشبكة متنامية من الوسطاء والمسوّقين والمعلّمين والمجتمعات بهياكل عمولات تنافسية."],
  ["الشفافية", "تواصل واضح حول دورنا كوسيط معرّف وكيفية تقديم خدمات الوساطة."],
  ["التداول المسؤول", "نعزز مشاركة واعية بالمخاطر ولا نقدّم أبداً وعوداً بعوائد مضمونة أو خالية من المخاطر."],
];
const BLOCKS_EN = [
  ["Broker Selection", "We seek brokerage relationships based on factors including regulatory status, technology, market access, client support and operational capabilities — so you can make more informed choices."],
  ["Trading Technology", "Access professional trading platforms offered by selected brokerage partners, including MetaTrader and modern web and mobile solutions."],
  ["Client Support", "Receive dedicated assistance throughout the brokerage selection and onboarding journey, with a team that understands the region."],
  ["Market Access", "Explore forex, commodities, indices and share instruments through brokerage solutions available from our partners."],
  ["Education", "Build your market knowledge with structured lessons across beginner, intermediate and advanced levels."],
  ["Market Intelligence", "Stay informed with market updates, technical analysis and economic insights from our research desk."],
  ["Regional Expertise", "Benefit from a business focused on the MENA region while connecting traders to global markets."],
  ["Partnership Ecosystem", "Join a growing network of IBs, affiliates, educators and communities with competitive commercial structures."],
  ["Transparency", "Clear communication about our role as an Introducing Broker and how brokerage services are provided."],
  ["Responsible Trading", "We promote risk-aware participation and never make guaranteed-return or risk-free claims."],
];

export default function WhyMenaGroupFX() {
  const { L, isAr } = useLang();
  const P = L.pages.why;
  usePageSeo("why");
  const BLOCKS = isAr ? BLOCKS_AR : BLOCKS_EN;
  return (
    <>
      <PageHero
        overline={P.overline}
        title={P.title}
        description={P.desc}
        breadcrumbs={[{ label: L.common.home, to: "/" }, { label: L.nav.about }]}
      >
        <Button asChild size="lg" className="mt-8 bg-finblue hover:bg-finblueDark"><Link to="/broker-partners">{P.cta}</Link></Button>
      </PageHero>

      <section className="py-20 lg:py-28">
        <div className="container-tight grid gap-6 md:grid-cols-2">
          {BLOCKS.map((b, i) => {
            const Icon = ICONS[i];
            return (
              <div key={i} className="flex gap-5 rounded-xl border border-hair bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-md">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-canvas text-finblue">
                  <Icon className="h-6 w-6" strokeWidth={1.5} />
                </span>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-ink">{b[0]}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-subtle">{b[1]}</p>
                  {i % 3 === 0 && <Link to="/broker-partners" className="mt-3 inline-block text-sm font-semibold text-finblue">{L.common.learnMore} →</Link>}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <CTASection />
    </>
  );
}
